import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLiveLeaderboard, getLastCompletedEvent, getNextEvent, getMyLeagues, getLeaguePicks, getLeague } from '../services/api';
import Loading from '../components/Loading';
import NextEventCard from '../components/NextEventCard';
import PullToRefresh from '../components/PullToRefresh';
import UserHoverCard from '../components/UserHoverCard';
import OctaAvatar from '../components/OctaAvatar';
import { LbRow, LbPodium, avatarBg, ChevronIcon, CheckIcon, TrophyIcon, RefreshIcon, CalendarIcon } from '../components/LeaderboardParts';
import '../styles/app/leaderboard.css';

function LiveLeaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastCompletedEvent, setLastCompletedEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [completedEventData, setCompletedEventData] = useState(null);
  const [pools, setPools] = useState([]);

  const fetchData = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    try {
      const [response, lastCompletedRes, nextEventRes, poolsRes] = await Promise.all([
        getLiveLeaderboard(),
        getLastCompletedEvent().catch(() => ({ data: null })),
        getNextEvent().catch(() => ({ data: null })),
        getMyLeagues().catch(() => ({ data: [] }))
      ]);
      setData(response.data);
      setLastCompletedEvent(lastCompletedRes.data);
      setNextEvent(nextEventRes.data);
      setPools(poolsRes.data || []);
      setLastUpdated(new Date());

      // If current event is open/draft and we have a completed event, fetch completed event standings from picks
      const currentStatus = response.data?.event?.status;
      if (lastCompletedRes.data && (currentStatus === 'open' || currentStatus === 'draft' || !response.data?.event)) {
        // Fetch completed event standings from picks data for each pool
        const poolIds = poolsRes.data?.map(p => p.id) || [];
        if (poolIds.length > 0) {
          const completedDataPromises = poolIds.map(async (poolId) => {
            try {
              const [picksRes, poolRes] = await Promise.all([
                getLeaguePicks(poolId, lastCompletedRes.data.id),
                getLeague(poolId)
              ]);
              const responseData = picksRes.data || {};
              const users = responseData.users || [];
              const fightsInfo = responseData.fights || {};
              const pool = poolRes.data;

              // Build standings from picks data
              const memberStats = {};
              pool?.members?.forEach(m => {
                memberStats[m.id] = {
                  user_id: m.id,
                  username: m.username,
                  display_name: m.display_name,
                  correct_picks: 0,
                  total_points: 0,
                  method_bonus: 0
                };
              });

              users.forEach(userData => {
                if (memberStats[userData.id]) {
                  const userPicks = userData.picks || [];
                  userPicks.forEach(pick => {
                    if (pick.is_correct) {
                      memberStats[userData.id].correct_picks++;
                      memberStats[userData.id].total_points += pick.points_earned || 0;
                      memberStats[userData.id].method_bonus += pick.method_bonus || 0;
                    }
                  });
                }
              });

              const standings = Object.values(memberStats)
                .sort((a, b) => b.total_points - a.total_points)
                .map((entry, index) => ({ ...entry, rank: index + 1 }));

              return {
                poolId,
                name: pool?.name || 'Unknown Pool',
                standings,
                fights: fightsInfo
              };
            } catch (err) {
              return { poolId, name: 'Unknown Pool', standings: [], fights: { total: 0, completed: 0 } };
            }
          });
          const completedResults = await Promise.all(completedDataPromises);
          setCompletedEventData(completedResults);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load live leaderboard');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return <Loading message="Loading live leaderboard..." />;
  }

  if (error) {
    return (
      <div className="container lb-page">
        <div className="pnp-empty lb-empty-wrap">
          <div className="pnp-empty-icon"><RefreshIcon /></div>
          <h3 className="pnp-empty-title">Unable to load live leaderboard</h3>
          <p>{error}</p>
          <div className="pnp-empty-actions">
            <button className="btn btn-secondary" onClick={() => fetchData(true)}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const event = data?.event;
  const fights = data?.fights || { total: 0, completed: 0 };
  const leagues = data?.leagues || [];
  const isLive = (event?.status === 'locked' && event?.date && new Date(event.date) <= new Date()) || (fights.completed > 0 && fights.completed < fights.total);

  // Determine if we should show completed event results instead
  // Only show completed results for 24 hours after completion
  const completedTimestamp = lastCompletedEvent?.completed_at || lastCompletedEvent?.date;
  const isCompletedWithin24Hours = lastCompletedEvent && completedTimestamp &&
    (new Date().getTime() - new Date(completedTimestamp).getTime()) < 24 * 60 * 60 * 1000;
  const showCompletedResults = isCompletedWithin24Hours && (event?.status === 'open' || event?.status === 'draft' || !event);
  const displayEvent = showCompletedResults ? lastCompletedEvent : event;

  // Get standings for completed event display
  const getCompletedEventStandings = (poolId) => {
    if (!completedEventData) return { standings: [], fights: { total: 0, completed: 0 }, name: '' };
    const poolData = completedEventData.find(p => p.poolId === poolId);
    return {
      standings: poolData?.standings || [],
      fights: poolData?.fights || { total: 0, completed: 0 },
      name: poolData?.name || ''
    };
  };

  const showPicksColumn = isLive || showCompletedResults;

  return (
    <PullToRefresh onRefresh={() => fetchData(false)}>
    <div className="container lb-page">
      {/* Header */}
      <header className="lb-head pnp-rise">
        <div className="lb-head-titles">
          <div className="lb-head-row">
            <h1 className="pnp-title">Leaderboard</h1>
            {isLive && (
              <span className="pnp-live-pill lb-live-pill-lg">
                <span className="pnp-live-dot" />
                Live
              </span>
            )}
          </div>
          <p className="pnp-page-sub">
            {event ? 'Track standings across all your pools in real-time' : 'No active event'}
          </p>
        </div>
        <button
          className={`btn btn-secondary lb-refresh${isRefreshing ? ' is-spinning' : ''}`}
          onClick={() => fetchData(false)}
          disabled={isRefreshing}
        >
          <RefreshIcon />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {!event && !showCompletedResults ? (
        <div className="pnp-empty lb-empty-wrap pnp-rise" style={{ '--i': 1 }}>
          <div className="pnp-glow" aria-hidden="true" />
          <div className="pnp-empty-icon"><CalendarIcon /></div>
          <h2 className="pnp-empty-title">No Active Event</h2>
          <p>
            Once a UFC card goes live, Pick 'N Pray will show real-time standings across all your pools here.
          </p>
        </div>
      ) : (leagues.length === 0 && pools.length === 0) ? (
        <div className="pnp-empty lb-empty-wrap pnp-rise" style={{ '--i': 1 }}>
          <div className="pnp-glow" aria-hidden="true" />
          <div className="pnp-empty-icon"><TrophyIcon /></div>
          <h2 className="pnp-empty-title">Join the Action</h2>
          <p>
            Create or join a pool to compete with friends and track your picks live.
          </p>
          <div className="pnp-empty-actions">
            <Link to="/join" className="btn btn-secondary">Join Pool</Link>
            <Link to="/create-pool" className="btn btn-primary">Create Pool</Link>
          </div>
        </div>
      ) : (
        <>
          {/* Event Hero Card */}
          <section className={`lb-hero pnp-rise${isLive ? ' is-live' : showCompletedResults ? ' is-final' : ''}`} style={{ '--i': 1 }}>
            <div className="pnp-glow lb-hero-glow" aria-hidden="true" />
            {isLive && <span className="pnp-claw" aria-hidden="true" />}
            <div className="lb-hero-inner">
              <div className="lb-hero-main">
                <div className="lb-hero-status">
                  {isLive ? (
                    <span className="pnp-live-pill lb-live-pill-lg"><span className="pnp-live-dot" />Live Now</span>
                  ) : showCompletedResults ? (
                    <span className="pnp-chip pnp-chip-green"><CheckIcon />Event Completed</span>
                  ) : (
                    <span className="pnp-eyebrow">Current Event</span>
                  )}
                </div>
                <h2 className="pnp-title lb-hero-name">{displayEvent?.name}</h2>
                <p className="lb-hero-date">
                  {displayEvent && new Date(displayEvent.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
              {!showCompletedResults && (
                <div className="lb-hero-score">
                  <span className="lb-hero-score-label">Fights</span>
                  <div className="lb-hero-score-value">
                    {fights.completed}<small>/{fights.total}</small>
                  </div>
                </div>
              )}
              {showCompletedResults && (
                <div className="lb-hero-score">
                  <span className="lb-hero-score-label">Final</span>
                  <span className="lb-hero-trophy"><TrophyIcon /></span>
                </div>
              )}
            </div>
            {/* Segmented fight progress */}
            {!showCompletedResults && fights.total > 0 && (
              <div className="lb-pips" aria-label={`${fights.completed} of ${fights.total} fights completed`}>
                {Array.from({ length: fights.total }).map((_, i) => (
                  <span key={i} className={`lb-pip${i < fights.completed ? ' is-done' : isLive && i === fights.completed ? ' is-next' : ''}`} />
                ))}
              </div>
            )}
            {showCompletedResults && (
              <div className="lb-bar is-final"><span style={{ width: '100%' }} /></div>
            )}
          </section>

          {/* Next Event Card - Show when current is completed */}
          {showCompletedResults && nextEvent && (
            <div className="lb-extras">
              <NextEventCard event={nextEvent} />
            </div>
          )}

          {/* Last updated */}
          {lastUpdated && (
            <div className="lb-meta">
              <span className="lb-meta-item">
                {isLive && <span className="pnp-live-dot" />}
                Last updated: <span className="pnp-num">{lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}</span>
              </span>
              <span className="lb-meta-item">Auto-refreshes every 30 seconds</span>
            </div>
          )}

          {/* Pool Leaderboards */}
          <div className="lb-pools">
            {(showCompletedResults ? pools : leagues).map((league, poolIndex) => {
              // When showing completed results, get standings from completedEventData
              const completedData = showCompletedResults ? getCompletedEventStandings(league.id) : null;
              const displayStandings = showCompletedResults
                ? completedData.standings
                : league.standings || [];
              const completedFightsCount = completedData?.fights?.completed || completedData?.fights?.total || 0;
              // For completed event data, use completedFightsCount
              const totalFightsForDisplay = showCompletedResults
                ? completedFightsCount
                : (league.fights?.completed ?? fights.completed);
              const anyPoints = (displayStandings[0]?.total_points || 0) > 0;
              // Skip the podium (and medals) while everyone is still tied at 0
              const hasPodium = displayStandings.length >= 3 && anyPoints;

              return (
              <section key={league.id} className="card lb-card pnp-rise" style={{ '--i': Math.min(poolIndex + 2, 6) }}>
                {/* Pool Header */}
                <div className="lb-card-head">
                  <div className="lb-card-titles">
                    <Link to={`/pool/${league.id}/leaderboard`} className="lb-pool-link">
                      <h3 className="lb-card-title">
                        {showCompletedResults ? completedData.name : league.name}
                      </h3>
                      <ChevronIcon className="lb-chev" />
                    </Link>
                    <p className="lb-card-sub">
                      <span className="pnp-num">{displayStandings.length}</span> member{displayStandings.length !== 1 ? 's' : ''} {showCompletedResults ? 'placed' : 'competing'}
                    </p>
                  </div>
                  <div className="lb-card-actions">
                    <Link to={`/pool/${league.id}/picks`} className="btn btn-secondary">
                      View Picks
                    </Link>
                    <Link to={`/pool/${league.id}`} className="btn btn-primary">
                      Pool Details
                    </Link>
                  </div>
                </div>

                <div className="lb-card-body">
                  {displayStandings.length === 0 ? (
                    <div className="lb-empty-inline">
                      {showCompletedResults ? 'No results available for this event.' : 'No picks submitted yet. Be the first!'}
                    </div>
                  ) : (
                    <div className={`lb-split${hasPodium ? ' has-podium' : ''}`}>
                      {/* Podium — top 3 */}
                      {hasPodium && (
                        <LbPodium entries={displayStandings} getPts={(e) => e.total_points || 0} userId={user?.id} />
                      )}

                      {/* Standings rows */}
                      <div className={`lb-rows${showPicksColumn ? '' : ' no-picks'}`}>
                        <div className="lb-row lb-row-head">
                          <span>#</span>
                          <span className="lb-h-avatar" aria-hidden="true" />
                          <span className="lb-h-player">Player</span>
                          <span className="lb-h-stat">Record</span>
                          <span className="lb-h-stat">Method</span>
                          <span className="lb-h-pts">Pts</span>
                          {showPicksColumn && <span>Picks</span>}
                        </div>
                        {displayStandings.map((entry, index) => {
                          const isCurrentUser = entry.user_id === user?.id;
                          // For completed events, calculate rank from index if not provided
                          const rank = entry.rank || (index + 1);

                          return (
                            <LbRow key={entry.user_id} rank={rank} scope={isLive && !showCompletedResults ? 'live' : null} index={index} isMe={isCurrentUser} plain={!anyPoints}>
                              <div className="lb-avatar">
                                <OctaAvatar name={entry.username} size={34} bg={avatarBg(entry.user_id, isCurrentUser)} />
                              </div>
                              <div className="lb-player">
                                <div className="lb-name-line">
                                  <span className="lb-name">
                                    <UserHoverCard userId={entry.user_id} username={entry.username}>
                                      <span>{entry.username}</span>
                                    </UserHoverCard>
                                  </span>
                                  {isCurrentUser && <span className="pnp-chip lb-you">You</span>}
                                </div>
                                <div className="lb-sub lb-sub-mobile">
                                  <span><span className="pnp-num">{entry.correct_picks || 0}/{totalFightsForDisplay}</span> record</span>
                                  <span className="is-method"><span className="pnp-num">+{entry.method_bonus || 0}</span> method</span>
                                </div>
                              </div>
                              <span className="lb-cell">{entry.correct_picks || 0}/{totalFightsForDisplay}</span>
                              <span className="lb-cell is-method">+{entry.method_bonus || 0}</span>
                              <span className="lb-pts">{entry.total_points || 0}</span>
                              {showPicksColumn && (
                                <Link
                                  className="lb-view"
                                  aria-label={`View ${entry.username}'s picks`}
                                  to={`/pool/${league.id}/picks?event=${showCompletedResults ? lastCompletedEvent?.id : event?.id}&user=${entry.user_id}`}
                                >
                                  <span><em className="lb-view-text">View</em><ChevronIcon /></span>
                                </Link>
                              )}
                            </LbRow>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
            })}
          </div>
        </>
      )}
    </div>
    </PullToRefresh>
  );
}

export default LiveLeaderboard;
